
// MARK: Api
import * as api from "@startapp/mistral-admin-api";

// MARK: Mobx
import { observable, action, computed } from "mobx";

// MARK: Components
import { IRowItem, ColumnType } from "../../components/Table/TableRow";

// MARK: Stores
import VariableChangeHandler from "../../resources/VariableChangeHandler";
import { uiStore, routerStore, languageStore } from "../_rootStore";
import ImageService from "../../services/ImageService";

// MARK: Resources
import strings from "../../resources/strings";

export default class GalleryStore extends VariableChangeHandler {
	// Services
	public imageService: ImageService = new ImageService();
	public mobileImageService: ImageService = new ImageService();

	// Control
	@observable public loading: boolean = false;
	@observable public onEditMode: boolean = false;
	@observable public pageOffSet: number = 0;

	@observable public galleries: api.Gallery[] = [];
	@observable public selectedGallery: api.Gallery | null = null;

	// Variables
	@observable public title: string = "";
	@observable public nick: string = "";
	@observable public description: string = "";
	@observable public url: string = "";

	@computed
	public get header() {
		const header = strings.gallery.table.header;
		return [
			header.picture,
			header.nick,
			header.title,
			header.description,
		];
	}

	private static formatGallery(gallery: api.Gallery): IRowItem {
		const galleryInSelectedLanguage = gallery.languages.filter((galleryLanguage) =>
		galleryLanguage.language === languageStore.selectedLanguage)[0].translation;

		return {
			id: gallery.id,
			name: galleryInSelectedLanguage.title,
			columns: [
				{
					value: galleryInSelectedLanguage.image.url,
					type: ColumnType.avatar,
				},
				{
					value: galleryInSelectedLanguage.nick,
				},
				{
					value: galleryInSelectedLanguage.title,
				},
				{
					value: galleryInSelectedLanguage.description,
				},
			],
		};
	}

	@computed
	public get galleriesTableRows(): IRowItem[] {
		return this.galleries.map(GalleryStore.formatGallery);
	}

	@action
	public getGallery = async (galleryId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			this.selectedGallery = await api.getPicture(galleryId);
			this.setEditorFields(this.selectedGallery);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public getGalleries = async (pageOffSet?: number) => {
		if (this.loading) {
			return;
		}

		if (!pageOffSet) {
			pageOffSet = 0;
		}

		this.loading = true;

		try {
			this.galleries = await api.getGallery(pageOffSet);
			this.pageOffSet = pageOffSet;
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public createOrEditGallery = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			if (this.imageService.singleImageUploader && this.imageService.singleImageUploader.uploadedUncertainImage) {
				if (!this.selectedGallery) {
					const newGallery: api.NewGallery = {
						title: this.title,
						nick: this.nick,
						description: this.description,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
					};
					this.selectedGallery = await api.createGallery(newGallery);
				} else {
					const editedGallery: api.EditGallery = {
						language: languageStore.selectedLanguage,
						nick: this.nick,
						title: this.title,
						description: this.description,
						image: this.imageService.singleImageUploader.uploadedUncertainImage,
					};
					this.selectedGallery = await api.editGallery(this.selectedGallery.id, editedGallery);
				}
				routerStore.replace("/dashboard/gallery");
				window.location.reload();
			} else {
				uiStore.showSnackbar(strings.gallery.createOrEdit.errors.imageNotFound);
			}
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public deleteGallery = async (galleryId: string) => {
		if (this.loading) {
			return;
		}

		this.loading = true;

		try {
			await api.deleteGallery(galleryId);
			this.galleries = this.galleries.filter((gallery) =>  gallery.id !== galleryId);
		} catch (e) {
			uiStore.showErrorSnackbar(e);
		} finally {
			this.loading = false;
		}
	};

	@action
	public openDeleteGalleryDialog = async (galleryId: string, galleryName: string) => {
		uiStore.showDialog({
			title: strings.gallery.deleteGalleryDialog.title,
			message: `${strings.gallery.deleteGalleryDialog.message}: ${galleryName}`,
		}, () => this.deleteGallery(galleryId));
	};

	@action
	public nextPage = async () => {
		await this.getGalleries(this.pageOffSet + 1);
	};

	@action
	public previousPage = async () => {
		if (this.pageOffSet > 0) {
			await this.getGalleries(this.pageOffSet - 1);
		}
	};

	@action
	public clear = () => {
		this.imageService.clear();
		this.mobileImageService.clear();
		this.title = "";
		this.nick = "";
		this.description = "";
		this.url = "";
	};

	@action
	public setEditorFields = (gallery: api.Gallery) => {
		const galleryInSelectedLanguage = gallery.languages.filter((galleryLanguage) =>
		galleryLanguage.language === languageStore.selectedLanguage)[0].translation;

		this.imageService.setSingleImage(galleryInSelectedLanguage.image);
		this.title = galleryInSelectedLanguage.title;
		this.nick = galleryInSelectedLanguage.nick;
		this.description = galleryInSelectedLanguage.description ? galleryInSelectedLanguage.description : "";
	};

	@action
	public startGalleryCreateOrEdit(galleryId?: string) {
		if (galleryId) {
			this.getGallery(galleryId);
			routerStore.push(`/dashboard/gallery/editor/${galleryId}`);
		} else {
			this.selectedGallery = null;
			this.clear();
			routerStore.push("/dashboard/gallery/editor/");
		}
	}

}
